// src/pages/admin/AdminBlogs.js - COMPLETE WITH HORIZONTAL SCROLL

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import DataTable from '../../components/admin/DataTable';
import AIFormAssistant from '../../components/admin/AIFormAssistant';
import ImageUpload from '../../components/admin/ImageUpload';
import { 
  fetchAdminBlogs, 
  createBlog, 
  updateBlog, 
  deleteBlog, 
  selectAdminBlogs, 
  selectAdminBlogsLoading,
  selectAdminError 
} from '../../redux/slices/adminSlice';

const AdminBlogs = () => {
  const dispatch = useDispatch();
  const blogs = useSelector(selectAdminBlogs);
  const loading = useSelector(selectAdminBlogsLoading);
  const error = useSelector(selectAdminError);

  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: 'lifestyle',
    status: 'draft',
    featured: false,
    featuredImage: '',
    tags: '',
    readTime: '5 min read',
    authorId: 1
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [contentAnalytics, setContentAnalytics] = useState({});

  useEffect(() => {
    dispatch(fetchAdminBlogs());
    loadContentAnalytics();
  }, [dispatch]);

  const loadContentAnalytics = () => {
    const mockAnalytics = {};
    blogs.forEach(blog => {
      mockAnalytics[blog.id] = {
        views: Math.floor(Math.random() * 10000) + 500,
        likes: Math.floor(Math.random() * 500) + 50,
        shares: Math.floor(Math.random() * 100) + 10,
        comments: Math.floor(Math.random() * 50) + 5,
        readTime: `${Math.floor(Math.random() * 8) + 2} min`,
        seoScore: Math.floor(Math.random() * 40) + 60,
        engagement: (Math.random() * 10 + 15).toFixed(1)
      };
    });
    setContentAnalytics(mockAnalytics);
  };

  const handleAIQuery = async (query) => {
    if (query.toLowerCase().includes('content strategy') || query.toLowerCase().includes('blog analytics')) {
      const totalBlogs = blogs.length;
      const publishedBlogs = blogs.filter(b => b.status === 'published').length;
      const totalViews = Object.values(contentAnalytics).reduce((sum, data) => sum + (data.views || 0), 0);
      const avgEngagement = totalBlogs > 0 
        ? Object.values(contentAnalytics).reduce((sum, data) => sum + parseFloat(data.engagement || 0), 0) / totalBlogs
        : 0;

      return `📊 **CONTENT ANALYTICS DASHBOARD**

**Blog Performance Overview:**
• Total Blog Posts: ${totalBlogs}
• Published Posts: ${publishedBlogs}
• Draft Posts: ${totalBlogs - publishedBlogs}
• Total Views: ${totalViews.toLocaleString()}
• Average Engagement: ${avgEngagement.toFixed(1)}%

**Top Performing Categories:**
🏠 Market Trends: 35% of total views
💰 Investment Tips: 28% of total views
🏘️ Lifestyle Content: 22% of total views
📈 Market Analysis: 15% of total views

**AI Recommendations:**
🎯 Focus on market trend content (highest engagement)
📝 Increase posting frequency to 3x per week
🔍 Optimize SEO for better search visibility
📱 Create more shareable social media content
💬 Enable comments to boost engagement

Ready to implement these content strategies?`;
    }

    if (query.toLowerCase().includes('blog post') || query.toLowerCase().includes('generate content') || query.toLowerCase().includes('write')) {
      const blogTypes = [
        'Market Trend Analysis',
        'Investment Guide',
        'First-Time Buyer Tips',
        'Neighborhood Spotlight',
        'Market Forecast',
        'Real Estate Technology',
        'Property Investment Strategy'
      ];
      const suggestedType = blogTypes[Math.floor(Math.random() * blogTypes.length)];

      return `✨ **AI BLOG CONTENT GENERATOR**

I'll help you create a comprehensive ${suggestedType.toLowerCase()} blog post. Here's what I can generate:

**📝 Content Structure:**
• **Engaging Headlines** - Multiple options with SEO optimization
• **Introduction** - Hook readers with compelling opening
• **Main Content** - Well-researched, structured sections
• **Call-to-Action** - Drive engagement and conversions

**Sample Topics I Can Create:**
🏠 "5 Emerging Neighborhoods to Watch in 2025"
💰 "Smart Investment Strategies for New Real Estate Investors"
📈 "Market Predictions: What to Expect This Quarter"

What type of blog post would you like me to create?`;
    }

    return `🤖 **BLOG MANAGEMENT AI**

I can help you with:
• ✍️ Generate complete blog posts on any real estate topic
• 📊 Content performance analytics and insights
• 🔍 SEO optimization and keyword research
• 📅 Content calendar planning

What aspect of your blog strategy would you like to explore?`;
  };

  const handleAIContentGeneration = () => {
    setAiSuggestions("🤖 AI is analyzing trends and generating comprehensive blog content...");
    
    setTimeout(() => {
      const contentTopics = [
        'Real Estate Market Trends 2025',
        'Smart Investment Strategies',
        'First-Time Buyer Guide',
        'Luxury Property Insights',
        'Neighborhood Development',
        'Property Technology Advances'
      ];
      
      const selectedTopic = formData.title || contentTopics[Math.floor(Math.random() * contentTopics.length)];
      
      const suggestions = {
        title: selectedTopic,
        excerpt: `Discover the latest insights about ${selectedTopic.toLowerCase()} and how they're shaping the real estate landscape.`,
        content: `# ${selectedTopic}

The real estate industry continues to evolve at an unprecedented pace, driven by changing consumer preferences, technological innovations, and economic shifts.

## Market Overview

Recent data indicates significant movement in several key areas of the real estate sector. Property values have shown resilience despite economic uncertainties.

### Key Market Indicators

- **Price Appreciation**: Average home values increased by 8.5% year-over-year
- **Inventory Levels**: Current supply remains 15% below historical averages
- **Days on Market**: Properties are selling 20% faster than last year
- **Interest Rates**: Current rates remain favorable for qualified buyers

## Expert Analysis

Industry professionals are optimistic about the market's trajectory, citing several positive indicators.

### Demand Drivers

1. **Demographic Shifts**: Millennials entering prime buying years
2. **Remote Work Impact**: Increased demand for suburban properties
3. **Investment Interest**: Strong institutional investor activity
4. **Technology Adoption**: Virtual tours and digital transactions

## Conclusion

${selectedTopic} represents both challenges and opportunities in today's dynamic real estate environment. Success requires staying informed, acting strategically, and adapting to changing market conditions.

*Ready to make your next real estate move? Contact our expert team for personalized guidance.*`,
        tags: ['real estate', 'market trends', 'investment', 'property', 'analysis', 'housing market'],
        category: formData.category || 'market-trends',
        readTime: '8 min read'
      };
      
      setAiSuggestions(suggestions);
    }, 3000);
  };

  const applyAISuggestions = () => {
    if (aiSuggestions && typeof aiSuggestions === 'object') {
      setFormData(prev => ({
        ...prev,
        title: aiSuggestions.title,
        excerpt: aiSuggestions.excerpt,
        content: aiSuggestions.content,
        tags: aiSuggestions.tags?.join(', '),
        category: aiSuggestions.category,
        readTime: aiSuggestions.readTime
      }));
      setAiSuggestions(null);
    }
  };

  // ✅ UPDATED: Enhanced blog columns with min-width
  const blogColumns = [
    {
      key: 'featured',
      label: '⭐',
      className: 'min-w-[60px]',
      render: (featured) => (
        <span className={`text-xl ${featured ? 'text-yellow-500' : 'text-gray-300'}`}>
          {featured ? '⭐' : '☆'}
        </span>
      )
    },
    {
      key: 'title',
      label: 'Title',
      className: 'min-w-[300px]',
      render: (title, blog) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
          <p className="text-sm text-gray-500 truncate">{blog.excerpt || 'No excerpt available'}</p>
        </div>
      )
    },
    {
      key: 'category',
      label: 'Category',
      className: 'min-w-[120px]',
      render: (category) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap">
          {category || 'lifestyle'}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      className: 'min-w-[100px]',
      render: (status) => {
        const statusColors = {
          published: 'bg-green-100 text-green-800',
          draft: 'bg-yellow-100 text-yellow-800',
          archived: 'bg-gray-100 text-gray-800'
        };
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusColors[status] || statusColors.draft}`}>
            {status || 'draft'}
          </span>
        );
      }
    },
    {
      key: 'publishedAt',
      label: 'Published',
      className: 'min-w-[120px]',
      render: (publishedAt) => (
        <span className="whitespace-nowrap">
          {publishedAt ? new Date(publishedAt).toLocaleDateString() : 'Not published'}
        </span>
      )
    },
    {
      key: 'views',
      label: 'Views',
      className: 'min-w-[80px]',
      render: (views, blog) => (
        <span className="whitespace-nowrap">
          {contentAnalytics[blog.id]?.views?.toLocaleString() || '0'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'min-w-[280px]',
      render: (_, blog) => (
        <div className="flex items-center space-x-2 whitespace-nowrap">
          <button
            onClick={() => handleEdit(blog)}
            className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </button>
          
          <button
            onClick={() => handleToggleStatus(blog)}
            className={`inline-flex items-center px-2 py-1 border shadow-sm text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
              blog.status === 'published'
                ? 'border-yellow-300 text-yellow-700 bg-yellow-50 hover:bg-yellow-100'
                : 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100'
            }`}
          >
            <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d={blog.status === 'published' 
                  ? "M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  : "M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                } 
              />
            </svg>
            {blog.status === 'published' ? 'Unpublish' : 'Publish'}
          </button>
          
          <button
            onClick={() => handleDelete(blog.id)}
            className="inline-flex items-center px-2 py-1 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete
          </button>
        </div>
      )
    }
  ];

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      category: 'lifestyle',
      status: 'draft',
      featured: false,
      featuredImage: '',
      tags: '',
      readTime: '5 min read',
      authorId: 1
    });
    setErrors({});
    setAiSuggestions(null);
  };

  const handleModalOpen = (blog = null) => {
    if (blog) {
      setEditingBlog(blog);
      setFormData({
        ...blog,
        tags: Array.isArray(blog.tags) ? blog.tags.join(', ') : blog.tags || ''
      });
    } else {
      setEditingBlog(null);
      resetForm();
    }
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingBlog(null);
    resetForm();
  };

  const handleEdit = (blog) => {
    handleModalOpen(blog);
  };

  const handleToggleStatus = async (blog) => {
    try {
      const newStatus = blog.status === 'published' ? 'draft' : 'published';
      await dispatch(updateBlog({ 
        id: blog.id, 
        status: newStatus,
        publishedAt: newStatus === 'published' ? new Date().toISOString() : null
      })).unwrap();
      dispatch(fetchAdminBlogs());
    } catch (error) {
      console.error('Error toggling blog status:', error);
    }
  };

  const handleDelete = async (blogId) => {
    if (window.confirm('Are you sure you want to delete this blog? This action cannot be undone.')) {
      try {
        await dispatch(deleteBlog(blogId)).unwrap();
        dispatch(fetchAdminBlogs());
      } catch (error) {
        console.error('Error deleting blog:', error);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.content?.trim()) {
      newErrors.content = 'Content is required';
    }
    
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const blogData = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : [],
        excerpt: formData.excerpt || formData.content?.substring(0, 200) + '...'
      };
      
      if (editingBlog) {
        await dispatch(updateBlog({ id: editingBlog.id, ...blogData })).unwrap();
      } else {
        await dispatch(createBlog(blogData)).unwrap();
      }
      
      handleModalClose();
      dispatch(fetchAdminBlogs());
    } catch (error) {
      console.error('Error saving blog:', error);
      setErrors({ submit: error.message || 'Failed to save blog' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.content?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || blog.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || blog.status === filterStatus;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 shadow rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Blog Management</h1>
            <p className="mt-2 text-indigo-100">
              AI-powered blog creation with intelligent content generation
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleModalOpen()}
            className="inline-flex items-center px-6 py-3 border border-transparent shadow-sm text-base font-medium rounded-md text-indigo-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Blog Post
          </motion.button>
        </div>

        {/* Stats Cards */}
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-3xl">📝</span>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-indigo-100 truncate">
                    All blog posts
                  </dt>
                  <dd className="text-2xl font-bold text-white">
                    {blogs.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-3xl">📊</span>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-indigo-100 truncate">
                    Published
                  </dt>
                  <dd className="text-2xl font-bold text-white">
                    {blogs.filter(b => b.status === 'published').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-3xl">👥</span>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-indigo-100 truncate">
                    Total Views
                  </dt>
                  <dd className="text-2xl font-bold text-white">
                    {Object.values(contentAnalytics).reduce((sum, data) => sum + (data.views || 0), 0).toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-3xl">⭐</span>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-indigo-100 truncate">
                    Featured
                  </dt>
                  <dd className="text-2xl font-bold text-white">
                    {blogs.filter(b => b.featured).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Search blogs
            </label>
            <input
              id="search"
              type="text"
              placeholder="Search by title or content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              id="category"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="all">All Categories</option>
              <option value="market-trends">Market Trends</option>
              <option value="buying-guide">Buying Guide</option>
              <option value="selling-tips">Selling Tips</option>
              <option value="investment">Investment</option>
              <option value="financing">Financing</option>
              <option value="lifestyle">Lifestyle</option>
              <option value="legal">Legal</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quick Actions
            </label>
            <div className="flex space-x-2">
              <button
                onClick={() => setFilterStatus('published')}
                className="flex-1 px-3 py-2 text-xs font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200"
              >
                Published
              </button>
              <button
                onClick={() => setFilterStatus('draft')}
                className="flex-1 px-3 py-2 text-xs font-medium text-yellow-700 bg-yellow-100 rounded-md hover:bg-yellow-200"
              >
                Drafts
              </button>
            </div>
          </div>
        </div>
      </div>

      

      {/* ✅ UPDATED: Data Table with Horizontal Scroll */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Blog Posts ({filteredBlogs.length})
          </h3>
        </div>
        
        {/* ✅ Horizontal Scroll Wrapper with Custom Scrollbar */}
        <div className="overflow-x-auto">
          <style jsx>{`
            .table-scroll {
              overflow-x: auto;
              scrollbar-width: thin;
              scrollbar-color: #6366f1 #f1f5f9;
            }

            .table-scroll::-webkit-scrollbar {
              height: 8px;
            }

            .table-scroll::-webkit-scrollbar-track {
              background: #f1f5f9;
              border-radius: 4px;
            }

            .table-scroll::-webkit-scrollbar-thumb {
              background: linear-gradient(90deg, #6366f1, #8b5cf6);
              border-radius: 4px;
            }

            .table-scroll::-webkit-scrollbar-thumb:hover {
              background: linear-gradient(90deg, #4f46e5, #7c3aed);
            }
          `}</style>
          
          <div className="table-scroll">
            <DataTable
              columns={blogColumns}
              data={filteredBlogs}
              loading={loading}
              emptyMessage="No blogs found. Create your first blog post!"
            />
          </div>
        </div>
      </div>

      {/* Modal - Same as before, truncated for brevity */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-600 bg-opacity-50 h-full w-full z-50 flex justify-center items-start overflow-auto"
          >
            <div className="relative my-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white overflow-visible">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}
                  </h3>
                  <button
                    onClick={handleModalClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Scrollable Form Container */}
                <div className="max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* AI Content Generation */}
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-md font-medium text-purple-900">
                          🤖 AI-powered content creation and optimization
                        </h4>
                        <button
                          type="button"
                          onClick={handleAIContentGeneration}
                          className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700 transition-colors"
                        >
                          Generate Content
                        </button>
                      </div>
                      
                      {aiSuggestions && (
                        <div className="space-y-4">
                          {typeof aiSuggestions === 'string' ? (
                            <p className="text-sm text-purple-700">{aiSuggestions}</p>
                          ) : (
                            <div className="bg-white p-4 rounded-lg border">
                              <div className="flex items-center justify-between mb-3">
                                <h5 className="font-medium text-gray-900">
                                  Comprehensive content generation and optimization
                                </h5>
                                <button
                                  type="button"
                                  onClick={applyAISuggestions}
                                  className="px-4 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition-colors"
                                >
                                  Apply All Suggestions
                                </button>
                              </div>
                              
                              <div className="space-y-3">
                                <div>
                                  <strong>Title:</strong> {aiSuggestions.title}
                                </div>
                                <div>
                                  <strong>Excerpt:</strong> {aiSuggestions.excerpt}
                                </div>
                                <div>
                                  <strong>Content Preview:</strong>
                                  <p className="text-sm text-gray-600 mt-1">
                                    {aiSuggestions.content?.substring(0, 200)}...
                                  </p>
                                </div>
                                <div>
                                  <strong>Tags:</strong> {aiSuggestions.tags?.join(', ')}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Form Fields */}
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                          Title *
                        </label>
                        <input
                          type="text"
                          name="title"
                          id="title"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                            errors.title ? 'border-red-300' : ''
                          }`}
                          placeholder="Enter blog title..."
                        />
                        {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                      </div>

                      <div>
                        <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                          Category *
                        </label>
                        <select
                          id="category"
                          name="category"
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        >
                          <option value="market-trends">Market Trends</option>
                          <option value="buying-guide">Buying Guide</option>
                          <option value="selling-tips">Selling Tips</option>
                          <option value="investment">Investment</option>
                          <option value="financing">Financing</option>
                          <option value="lifestyle">Lifestyle</option>
                          <option value="legal">Legal</option>
                          <option value="maintenance">Maintenance</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                          Status
                        </label>
                        <select
                          id="status"
                          name="status"
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        >
                          <option value="draft">Draft</option>
                          <option value="published">Published</option>
                          <option value="archived">Archived</option>
                        </select>
                      </div>

                      <div className="sm:col-span-2">
                        <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700">
                          Excerpt
                        </label>
                        <textarea
                          id="excerpt"
                          name="excerpt"
                          rows={3}
                          value={formData.excerpt}
                          onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          placeholder="Brief description of the blog post..."
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                          Content *
                        </label>
                        <textarea
                          id="content"
                          name="content"
                          rows={12}
                          value={formData.content}
                          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                          className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                            errors.content ? 'border-red-300' : ''
                          }`}
                          placeholder="Write your blog content here... (Supports Markdown)"
                        />
                        {errors.content && <p className="mt-1 text-sm text-red-600">{errors.content}</p>}
                      </div>

                      <div>
                        <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
                          Tags
                        </label>
                        <input
                          type="text"
                          name="tags"
                          id="tags"
                          value={formData.tags}
                          onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          placeholder="tag1, tag2, tag3..."
                        />
                      </div>

                      <div>
                        <label htmlFor="readTime" className="block text-sm font-medium text-gray-700">
                          Read Time
                        </label>
                        <input
                          type="text"
                          name="readTime"
                          id="readTime"
                          value={formData.readTime}
                          onChange={(e) => setFormData({ ...formData, readTime: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          placeholder="5 min read"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <div className="flex items-center">
                          <input
                            id="featured"
                            name="featured"
                            type="checkbox"
                            checked={formData.featured}
                            onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <label htmlFor="featured" className="ml-2 block text-sm text-gray-900">
                            Featured post
                          </label>
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <ImageUpload
                          label="Featured Image"
                          value={formData.featuredImage}
                          onChange={(url) => setFormData({ ...formData, featuredImage: url })}
                          accept="image/*"
                        />
                      </div>
                    </div>

                    {errors.submit && (
                      <div className="bg-red-50 border border-red-200 rounded-md p-4">
                        <p className="text-sm text-red-600">{errors.submit}</p>
                      </div>
                    )}
                  </form>
                </div>

                {/* Fixed Form Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6 bg-white">
                  <button
                    type="button"
                    onClick={handleModalClose}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="submit"
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Saving...' : editingBlog ? 'Update Blog' : 'Create Blog'}
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminBlogs;
